import { api } from './api.js';

/**
 * Service for downloading PDF files via backend proxy
 * This bypasses Cloudinary's "Uploaded" restriction by using signed backend URLs
 */
class PdfDownloadService {
  /**
   * Get backend proxy URL for PDF
   * @param {string} publicId - Cloudinary public ID of the PDF
   * @returns {Promise<string>} - Backend proxy URL
   */
  async getProxyUrl(publicId) {
    try {
      console.log('Getting backend proxy URL for:', publicId);
      
      // The backend will return a proxy URL that bypasses Cloudinary restrictions
      const response = await api.get('/cloudflare-stream/get-pdf-url', {
        params: { publicId }
      });
      
      if (response.data && response.data.url) {
        console.log('Backend proxy URL received:', response.data.url);
        return response.data.url;
      } else {
        throw new Error('No URL returned from server');
      }
    } catch (error) {
      console.error('Error getting proxy URL:', error);
      
      if (error.response?.status === 403) {
        throw new Error('Access denied. This content requires basic tier subscription.');
      } else if (error.response?.status === 401) {
        throw new Error('Please log in to download this content.');
      } else {
        throw new Error('Failed to generate download link. Please try again.');
      }
    }
  }

  /**
   * Download PDF via backend proxy by fetching as blob
   * @param {string} publicId - Cloudinary public ID of the PDF
   * @param {string} filename - Name for the downloaded file
   */
  async downloadPdf(publicId, filename = 'fit-test-guide.pdf') {
    try {
      console.log('Starting PDF download for:', publicId);
      
      // Get the backend proxy URL
      const proxyUrl = await this.getProxyUrl(publicId);
      
      // Fetch the PDF as a blob
      console.log('Fetching PDF from:', proxyUrl);
      const response = await fetch(proxyUrl, {
        credentials: 'include' // Include cookies for authentication
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
      }
      
      // Get the blob
      const blob = await response.blob();
      console.log('PDF blob received, size:', blob.size);
      
      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      
      // Add to DOM, click, then remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a short delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      
      console.log('PDF download initiated successfully');
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }

  /**
   * Open PDF in new tab via backend proxy by fetching as blob
   * @param {string} publicId - Cloudinary public ID of the PDF
   */
  async openPdfInNewTab(publicId) {
    try {
      console.log('Opening PDF in new tab for:', publicId);
      
      // Get the backend proxy URL
      const proxyUrl = await this.getProxyUrl(publicId);
      
      // Fetch the PDF as a blob
      console.log('Fetching PDF from:', proxyUrl);
      const response = await fetch(proxyUrl, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
      }
      
      // Get the blob
      const blob = await response.blob();
      console.log('PDF blob received, size:', blob.size);
      
      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      
      console.log('PDF opened in new tab successfully');
      
    } catch (error) {
      console.error('Error opening PDF:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const pdfDownloadService = new PdfDownloadService();
export default pdfDownloadService;
